import { Tabs, Tab } from '@ds';
import { DataTable } from '@ui';
import { useCallback, useMemo } from 'react';
import { noop } from 'lodash-es';
import { toast } from 'react-toastify';
import {
  getNotificationsSettingsColumns,
  getRenderNotificationsSettingsCell,
} from '@features/config/notifications/notifications.config';
import { getCategoryEnabledEventsInfo } from '@/utils/helpers/notificationsSettings.helpers';
import {
  PersonalizationSettingsCategory,
  PersonalizationCategoryChannelUpdateData,
  PersonalizationEventChannelUpdateData,
} from '@/api/apis/communication/personalization';
import {
  useUpdatePersonalizationCategoryChannelMutation,
  useUpdatePersonalizationEventChannelMutation,
} from '@/queries/communication/personalization/personalization.mutation';
import { retrieveUnifiedError } from '@/api/utils';

type NotificationTabsProps = {
  categories: PersonalizationSettingsCategory[];
};

export const NotificationTabs = ({ categories }: NotificationTabsProps) => {
  const { mutateAsync: updatePersonalizationCategoryChannel } = useUpdatePersonalizationCategoryChannelMutation();
  const { mutateAsync: updatePersonalizationEventChannel } = useUpdatePersonalizationEventChannelMutation();

  const categoryChannelToggle = useCallback(
    async (data: PersonalizationCategoryChannelUpdateData) => {
      try {
        await updatePersonalizationCategoryChannel(data);
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [updatePersonalizationCategoryChannel],
  );

  const eventChannelToggle = useCallback(
    async (data: PersonalizationEventChannelUpdateData) => {
      try {
        await updatePersonalizationEventChannel(data);
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [updatePersonalizationEventChannel],
  );

  const renderNotificationsSettingsCell = useMemo(() => {
    return getRenderNotificationsSettingsCell(eventChannelToggle);
  }, [eventChannelToggle]);

  return (
    <div className="w-[90%] min-w-[700px] flex flex-1 flex-col gap-4 bg-base-70 rounded-xl relative max-h-[76vh]">
      <h1 className="pt-6 px-4 text-xl mb-2 absolute">Categories</h1>
      <Tabs
        aria-label="Tabs"
        isVertical
        variant="light"
        radius="none"
        classNames={{
          tabWrapper: 'flex-1',
          tabContent: 'text-m-cps',
          base: 'min-w-[240px] pt-16 pb-0 flex flex-col h-[76vh]',
          tabList: 'w-full px-0 gap-0 overflow-y-auto max-h-[76vh]',
          tab: 'h-[42px] py-0 px-4 justify-start min-h-[42px]',
          panel: 'w-full overflow-auto p-0 flex-1 max-h-[76vh]',
          cursor: 'w-full shadow-none',
        }}
      >
        {categories.map((category) => (
          <Tab
            key={category.name}
            title={
              <div className="flex gap-1 text-m-m normal-case">
                {category.label}
                <span className="text-m-r">({getCategoryEnabledEventsInfo(category)})</span>
              </div>
            }
            isDisabled={!category.editable}
          >
            <div className="flex flex-1 h-full">
              <DataTable
                hidePagination
                idKey="name"
                label="Notifications"
                columns={getNotificationsSettingsColumns(category, categoryChannelToggle)}
                renderCell={(notification, columnKey) =>
                  renderNotificationsSettingsCell(category, notification, columnKey)
                }
                data={category.notifications}
                isLoading={false}
                isFetching={false}
                totalItems={category.notifications.length}
                totalPages={1}
                page={0}
                itemsPerPage={category.notifications.length}
                wrapperClassName="flex flex-1 rounded-tl-none rounded-bl-none rounded-tr-xl rounded-br-xl shadow-none"
                baseClassName="h-full"
                tdClassName="text-left"
                onPageChange={noop}
                onItemsPerPageChange={noop}
                onSortByChange={noop}
              />
            </div>
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};
